import { useEffect, useState } from 'react';

import type { IClient } from '~/types/client';
import { getSubscriptionStatus } from '~/services/subscription/getSubscriptionStatus';

export type DualScreenAccess = 'yes' | 'no' | 'loading';

/**
 * Statut d'essai lu une fois par établissement et par page (appel au serveur de paiement).
 * `null` = illisible (réseau, connexion pas encore rétablie) : pas mis en cache, on
 * redemandera à la prochaine ouverture.
 */
const trialCache = new Map<string, Promise<boolean | null>>();

function isTrialing(clientID: string) {
    let p = trialCache.get(clientID);
    if (!p) {
        p = getSubscriptionStatus(clientID).then((s) => {
            if (!s) {
                trialCache.delete(clientID);
                return null;
            }
            return s.status === 'trialing';
        });
        trialCache.set(clientID, p);
    }
    return p;
}

/**
 * Double écran « Régie » : formule Pro, et offert pendant l'essai pour que les
 * animateurs le découvrent (décision 30/10/2026). Contrôle d'affichage seulement :
 * rien de payant ni de sensible derrière (la marque Pro, elle, est verrouillée
 * dans les règles Firestore). Statut illisible → ouvert : un essai sur un wifi de bar
 * capricieux ne doit pas tomber sur la page « Passez en Pro ».
 */
export function useDualScreenAccess(client: IClient | undefined, enabled = true): DualScreenAccess {
    const pro = client?.plan === 'pro';
    // L'objectif « 3 soirées » n'existe que pendant un essai : réponse immédiate sans réseau.
    const trialGoal = !!client?.trialGoal;
    const clientID = client?.id;
    // undefined = en cours, null = illisible.
    const [trialing, setTrialing] = useState<boolean | null | undefined>(undefined);

    useEffect(() => {
        if (!enabled || !clientID || pro || trialGoal || client?.isDemo()) return undefined;
        let alive = true;
        setTrialing(undefined);
        isTrialing(clientID).then((v) => {
            if (alive) setTrialing(v);
        });
        return () => {
            alive = false;
        };
    }, [clientID, pro, trialGoal, enabled]);

    if (!client) return 'loading';
    if (client.isDemo()) return 'no';
    if (pro || trialGoal) return 'yes';
    if (trialing === undefined) return 'loading';
    return trialing === false ? 'no' : 'yes';
}
